import React, { useRef } from 'react'

import { Account } from '@driftt/api-types'
import { Box, Button, Flex, Header, InfoBadge, Link, StatusBadge, Text } from '@driftt/dds'
import { faLinkedinIn } from '@driftt/dds.icons/brands/faLinkedinIn'
import { faSalesforce } from '@driftt/dds.icons/brands/faSalesforce'
import { faTwitter } from '@driftt/dds.icons/brands/faTwitter'

import { AccountActivityAccountAvatar } from 'components/Avatars'
import EngagementScore from 'components/EngagementScore'
import { CompanyData, FormatLastTouched } from 'modules/accountActivity/domain'
import { formatLastTouched } from 'utils/activity'
import { timeSinceUpdated } from 'utils/timeStamp'
import { segmentTrack, TrackType } from 'utils/tracking'

import SocialLinkIconButtons from './SocialLinkIconButton'

const LINKEDIN_URL = 'https://www.linkedin.com/'
const TWITTER_URL = 'https://www.twitter.com/'

interface CompanyInfoRowProps {
  account: Account | undefined
  company: CompanyData | undefined
  explainData: any | undefined
  salesforceData: any | undefined
}

const CompanyInfoRow = ({ account, explainData, company, salesforceData }: CompanyInfoRowProps) => {
  const engagementRef = useRef<HTMLDivElement>(null)
  const salesforceAccount = salesforceData?.accounts ? salesforceData?.accounts[0] : {}

  const {
    icon: lastTouchIcon,
    sender,
    actionType,
    action,
    recipient,
    union,
    showBlank
  } = formatLastTouched(explainData?.driftSfdcTask ?? null) as FormatLastTouched

  return (
    <Flex
      fullWidth
      alignItems="center"
      justifyContent="space-between"
      css={{ borderBottom: '1px solid $gray3', paddingBottom: '$2' }}>
      <Flex alignItems="center" justifyContent="center" gap={2}>
        <AccountActivityAccountAvatar imageUrl={company?.logo} size="large" inCrm={!!account} />
        <Box>
          <Flex alignItems="center" gap={2}>
            <Header variant="h1">{account?.name || company?.name || company?.domain}</Header>
            <EngagementScore
              boundaryRef={engagementRef.current}
              accountActivity={explainData}
              placement="bottom-end"
              showMore={false}
            />
          </Flex>
          <Flex direction="column" gap={1}>
            <Text variant="body" css={{ marginBottom: '4px' }}>
              {company?.about}
            </Text>
            {!account && (
              <StatusBadge variant="warning">Account record was not found in Drift CRM</StatusBadge>
            )}
            {!showBlank && (
              <Link
                external
                onClick={() =>
                  segmentTrack(TrackType.LAST_TOUCH_NAVIGATES_TO_SALESFORCE_FROM_ACTIVITY_PAGE)
                }
                truncate
                href={`${salesforceData?.baseUrl}lightning/r/Task/${explainData.driftSfdcTask.taskId}/view`}
                target="_blank">
                <InfoBadge icon={lastTouchIcon} variant="primary" size="medium">
                  {`${sender} ${action} ${actionType} ${union} ${recipient} - ${timeSinceUpdated(
                    explainData.driftSfdcTask.activityAt
                  )} ago`}
                </InfoBadge>
              </Link>
            )}
          </Flex>
        </Box>
      </Flex>
      <Flex>
        <SocialLinkIconButtons
          validation={salesforceData?.baseUrl && salesforceAccount}
          trackType={TrackType.ACCOUNT_DETAILS_HEADER_NAVIGATES_TO_SALESFORCE}
          href={`${salesforceData?.baseUrl}lightning/r/Account/${salesforceAccount?.Id}/view`}
          tooltip="Salesforce"
          icon={faSalesforce}
        />
        <SocialLinkIconButtons
          validation={!!company?.linkedinHandle}
          trackType={TrackType.ACCOUNT_DETAILS_HEADER_NAVIGATES_TO_LINKEDIN}
          href={`${LINKEDIN_URL}${company?.linkedinHandle?.toLocaleLowerCase()}`}
          tooltip="LinkedIn"
          icon={faLinkedinIn}
        />
        <SocialLinkIconButtons
          validation={!!company?.twitterHandle}
          trackType={TrackType.ACCOUNT_DETAILS_HEADER_NAVIGATES_TWITTER}
          href={`${TWITTER_URL}${company?.twitterHandle}`}
          tooltip="Twitter"
          icon={faTwitter}
        />
        <Link
          css={{
            '&:hover': {
              textDecoration: 'none'
            }
          }}
          external
          onClick={() => segmentTrack(TrackType.ACCOUNT_DETAILS_HEADER_NAVIGATES_ACCOUNT_PAGE)}
          truncate
          href={`https://www.${company?.domain || account?.domain}`}
          target="_blank">
          <Button variant="secondary">Go to {company?.domain || account?.domain}</Button>
        </Link>
      </Flex>
    </Flex>
  )
}

export default CompanyInfoRow
